import { useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./SettingsPage.css";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:5050";

const TABS = ["Basic Info", "Contact", "Operations", "AI", "Quick Answers", "Integrations"] as const;
type Tab = typeof TABS[number];

// ── FAQ types ──────────────────────────────────────────────
const FAQ_MAX = 500;
const FAQ_WARN = 400;
const FAQ_TOTAL_CAP = 2000;
const FAQ_LANGS = ["English", "Arabic", "French", "German", "Spanish", "Italian"];

interface FaqEntry {
  id: number;
  hotel_id: string;
  faq_summary: string;
  faq_content: string;
  language: string;
  active: boolean;
  sort_order: number;
  updated_at: string;
}

// ─── Data constants ───────────────────────────────────────────────────────────

const FACILITIES_LIST = [
  "24-Hour Front Desk", "Airport Shuttle", "ATM on Site", "Bar & Lounge",
  "Beach Access", "Beach Club", "Boat Rentals", "Breakfast Included",
  "Business Center", "Casino", "Children's Pool", "Children's Play Area",
  "Concierge Service", "Conference Rooms", "Currency Exchange", "Cycling",
  "Dive Center", "Dry Cleaning", "EV Charging", "Elevator", "Fitness Center",
  "Garden", "Golf Course", "Hot Tub / Jacuzzi", "Kids Club", "Laundry Service",
  "Luggage Storage", "Mini Golf", "Non-smoking Rooms", "Outdoor Cinema",
  "Parking", "Pet Friendly", "Pool — Heated", "Pool — Indoor", "Pool — Outdoor",
  "Restaurant", "Rooftop Bar", "Rooftop Terrace", "Room Service", "Sauna",
  "Shopping", "Spa", "Steam Room", "Swim-up Bar", "Tennis Court", "Tour Desk",
  "Valet Parking", "Water Sports", "Wheelchair Accessible", "WiFi — Free",
  "Yoga Studio",
];

const CURRENCIES = [
  { value: "EGP", label: "EGP — Egyptian Pound" },
  { value: "USD", label: "USD — US Dollar" },
  { value: "EUR", label: "EUR — Euro" },
  { value: "GBP", label: "GBP — British Pound" },
  { value: "AED", label: "AED — UAE Dirham" },
  { value: "SAR", label: "SAR — Saudi Riyal" },
  { value: "JOD", label: "JOD — Jordanian Dinar" },
  { value: "KWD", label: "KWD — Kuwaiti Dinar" },
  { value: "BHD", label: "BHD — Bahraini Dinar" },
  { value: "QAR", label: "QAR — Qatari Riyal" },
  { value: "OMR", label: "OMR — Omani Rial" },
  { value: "LBP", label: "LBP — Lebanese Pound" },
  { value: "MAD", label: "MAD — Moroccan Dirham" },
  { value: "TND", label: "TND — Tunisian Dinar" },
  { value: "CHF", label: "CHF — Swiss Franc" },
  { value: "JPY", label: "JPY — Japanese Yen" },
  { value: "CNY", label: "CNY — Chinese Yuan" },
  { value: "AUD", label: "AUD — Australian Dollar" },
  { value: "CAD", label: "CAD — Canadian Dollar" },
  { value: "INR", label: "INR — Indian Rupee" },
  { value: "TRY", label: "TRY — Turkish Lira" },
];

const TIMEZONES = [
  { value: "Africa/Cairo",         label: "Africa/Cairo (UTC+2)" },
  { value: "Africa/Casablanca",    label: "Africa/Casablanca (UTC+0/+1)" },
  { value: "Africa/Johannesburg",  label: "Africa/Johannesburg (UTC+2)" },
  { value: "Africa/Nairobi",       label: "Africa/Nairobi (UTC+3)" },
  { value: "Africa/Tunis",         label: "Africa/Tunis (UTC+1)" },
  { value: "America/Chicago",      label: "America/Chicago (UTC-6/-5)" },
  { value: "America/Los_Angeles",  label: "America/Los_Angeles (UTC-8/-7)" },
  { value: "America/New_York",     label: "America/New_York (UTC-5/-4)" },
  { value: "America/Toronto",      label: "America/Toronto (UTC-5/-4)" },
  { value: "Asia/Beirut",          label: "Asia/Beirut (UTC+2/+3)" },
  { value: "Asia/Dubai",           label: "Asia/Dubai (UTC+4)" },
  { value: "Asia/Jerusalem",       label: "Asia/Jerusalem (UTC+2/+3)" },
  { value: "Asia/Kuwait",          label: "Asia/Kuwait (UTC+3)" },
  { value: "Asia/Riyadh",          label: "Asia/Riyadh (UTC+3)" },
  { value: "Asia/Singapore",       label: "Asia/Singapore (UTC+8)" },
  { value: "Asia/Tokyo",           label: "Asia/Tokyo (UTC+9)" },
  { value: "Europe/Amsterdam",     label: "Europe/Amsterdam (UTC+1/+2)" },
  { value: "Europe/Berlin",        label: "Europe/Berlin (UTC+1/+2)" },
  { value: "Europe/London",        label: "Europe/London (UTC+0/+1)" },
  { value: "Europe/Moscow",        label: "Europe/Moscow (UTC+3)" },
  { value: "Europe/Paris",         label: "Europe/Paris (UTC+1/+2)" },
  { value: "Pacific/Auckland",     label: "Pacific/Auckland (UTC+12/+13)" },
  { value: "Pacific/Sydney",       label: "Pacific/Sydney (UTC+10/+11)" },
];

const LANGUAGES = [
  { value: "English",    label: "English" },
  { value: "Arabic",     label: "Arabic" },
  { value: "French",     label: "French" },
  { value: "German",     label: "German" },
  { value: "Spanish",    label: "Spanish" },
  { value: "Italian",    label: "Italian" },
  { value: "Portuguese", label: "Portuguese" },
  { value: "Russian",    label: "Russian" },
  { value: "Chinese",    label: "Chinese (Mandarin)" },
  { value: "Japanese",   label: "Japanese" },
  { value: "Turkish",    label: "Turkish" },
  { value: "Dutch",      label: "Dutch" },
];

const PAYMENT_METHODS_LIST = [
  "Cash", "Visa", "Mastercard", "Amex", "Apple Pay",
  "Google Pay", "Bank Transfer", "Crypto", "PayPal",
];

// ─── Shared prop interfaces ───────────────────────────────────────────────────

interface FP {
  form: Record<string, any>;
  onChange: (key: string, val: any) => void;
}
interface FieldProps extends FP {
  label: string; name: string; type?: string;
  placeholder?: string; readOnly?: boolean; span2?: boolean;
}
interface SelectProps extends FP {
  label: string; name: string;
  options: { value: string; label: string }[];
  span2?: boolean;
}
interface ToggleProps extends FP {
  label: string; name: string; options: string[]; allowCustom?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Module-level components
// ─────────────────────────────────────────────────────────────────────────────

function Field({ label, name, type = "text", placeholder = "", readOnly = false, span2 = false, form, onChange }: FieldProps) {
  return (
    <div className={`sp-field${span2 ? " sp-field-span2" : ""}`}>
      <label className="sp-label">{label}</label>
      <input
        type={type}
        className={`sp-input${readOnly ? " sp-input-readonly" : ""}`}
        value={form[name] ?? ""}
        placeholder={placeholder || label}
        readOnly={readOnly}
        onChange={e => !readOnly && onChange(name, e.target.value)}
      />
    </div>
  );
}

function TextArea({ label, name, placeholder = "", rows = 4, form, onChange }: FP & { label: string; name: string; placeholder?: string; rows?: number }) {
  return (
    <div className="sp-field sp-field-span2">
      <label className="sp-label">{label}</label>
      <textarea
        className="sp-input sp-textarea"
        value={form[name] ?? ""}
        placeholder={placeholder || label}
        rows={rows}
        onChange={e => onChange(name, e.target.value)}
      />
    </div>
  );
}

function AutoField({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="sp-field">
      <label className="sp-label">{label} <span className="sp-auto-badge">Auto</span></label>
      <input type="text" className="sp-input sp-input-readonly" value={value ?? "—"} readOnly />
    </div>
  );
}

function StatusBadge({ label, configured }: { label: string; configured: boolean }) {
  return (
    <div className="sp-field">
      <label className="sp-label">{label}</label>
      <div className={`sp-status-badge ${configured ? "sp-status-ok" : "sp-status-missing"}`}>
        {configured ? "✓ Configured" : "✗ Not set"}
      </div>
    </div>
  );
}

function SelectField({ label, name, options, span2 = false, form, onChange }: SelectProps) {
  return (
    <div className={`sp-field${span2 ? " sp-field-span2" : ""}`}>
      <label className="sp-label">{label}</label>
      <select
        className="sp-input sp-select"
        value={form[name] ?? ""}
        onChange={e => onChange(name, e.target.value)}
      >
        <option value="">— Select —</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function ToggleGroup({ label, name, options, form, onChange, allowCustom }: ToggleProps) {
  const current = form[name] ?? "";
  return (
    <div className="sp-field">
      <label className="sp-label">{label}</label>
      <div className="sp-toggle-group">
        {options.map(opt => (
          <button
            key={opt}
            type="button"
            className={`sp-toggle-btn${current === opt ? " active" : ""}`}
            onClick={() => onChange(name, opt)}
          >
            {opt}
          </button>
        ))}
      </div>
      {allowCustom && (
        <input
          className="sp-input"
          style={{ marginTop: 8 }}
          value={current}
          placeholder="Or type a custom policy (e.g. Allowed with 500 EGP charge)…"
          onChange={e => onChange(name, e.target.value)}
        />
      )}
    </div>
  );
}

function PercentField({ label, name, form, onChange }: Omit<FieldProps, "type" | "placeholder" | "readOnly" | "span2">) {
  const raw = form[name];
  const display = raw != null ? Math.round(Number(raw) * 100) : "";
  return (
    <div className="sp-field">
      <label className="sp-label">{label}</label>
      <div className="sp-percent-wrap">
        <input
          type="number"
          className="sp-input"
          value={display}
          min={0}
          max={100}
          step={1}
          onChange={e => onChange(name, Number(e.target.value) / 100)}
        />
        <span className="sp-percent-sym">%</span>
      </div>
    </div>
  );
}

function PaymentMethodsSelect({ form, onChange }: FP) {
  const selected: string[] = form.accepted_payment_methods
    ? form.accepted_payment_methods.split(",").map((s: string) => s.trim()).filter(Boolean)
    : [];
  const toggle = (m: string) => {
    const next = selected.includes(m) ? selected.filter(s => s !== m) : [...selected, m];
    onChange("accepted_payment_methods", next.join(", "));
  };
  return (
    <div className="sp-field sp-field-span2">
      <label className="sp-label">Accepted Payment Methods</label>
      <div className="sp-tags-select">
        {PAYMENT_METHODS_LIST.map(m => (
          <button
            key={m}
            type="button"
            className={`sp-tag-btn${selected.includes(m) ? " active" : ""}`}
            onClick={() => toggle(m)}
          >
            {m}
          </button>
        ))}
      </div>
    </div>
  );
}

function OffersEditor({ form, onChange }: FP) {
  const [draft, setDraft] = useState("");
  const offers: string[] = form.offers_mentioned
    ? form.offers_mentioned.split(",").map((s: string) => s.trim()).filter(Boolean)
    : [];

  const add = () => {
    const val = draft.trim();
    if (!val) return;
    onChange("offers_mentioned", [...offers, val].join(", "));
    setDraft("");
  };
  const remove = (i: number) =>
    onChange("offers_mentioned", offers.filter((_, idx) => idx !== i).join(", "));

  return (
    <div className="sp-field sp-field-span2">
      <label className="sp-label">Offers &amp; Discounts</label>
      <div className="sp-offers-list">
        {offers.length === 0 && <p className="sp-offers-empty">No offers added yet — add one below</p>}
        {offers.map((o, i) => (
          <div key={i} className="sp-offer-row">
            <span className="sp-offer-text">{o}</span>
            <button type="button" className="sp-offer-del" onClick={() => remove(i)}>×</button>
          </div>
        ))}
      </div>
      <div className="sp-offers-add-row">
        <input
          className="sp-input"
          placeholder='e.g. 10% off for 3+ nights, Free breakfast for loyalty members…'
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <button type="button" className="sp-offers-add-btn" onClick={add} disabled={!draft.trim()}>
          + Add
        </button>
      </div>
    </div>
  );
}

function FacilitiesSelect({ form, onChange }: FP) {
  const [open, setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const [custom, setCustom] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected: string[] = form.facilities
    ? form.facilities.split(",").map((s: string) => s.trim()).filter(Boolean)
    : [];

  const filtered = FACILITIES_LIST.filter(
    f => f.toLowerCase().includes(search.toLowerCase()) && !selected.includes(f)
  );

  const toggle = (item: string) => {
    const next = selected.includes(item)
      ? selected.filter(s => s !== item)
      : [...selected, item];
    onChange("facilities", next.join(", "));
  };

  const addCustom = () => {
    const val = custom.trim();
    if (val && !selected.includes(val)) onChange("facilities", [...selected, val].join(", "));
    setCustom("");
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false); setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="sp-field sp-field-span2 sp-fac-wrap" ref={wrapRef}>
      <label className="sp-label">Facilities</label>
      <div className={`sp-fac-box${open ? " sp-fac-box-open" : ""}`} onClick={() => setOpen(true)}>
        {selected.length === 0 && <span className="sp-fac-placeholder">Click to select facilities…</span>}
        {selected.map(f => (
          <span key={f} className="sp-fac-tag">
            {f}
            <button type="button" className="sp-fac-tag-x" onClick={e => { e.stopPropagation(); toggle(f); }}>×</button>
          </span>
        ))}
      </div>

      {open && (
        <div className="sp-fac-dropdown">
          <input
            autoFocus
            className="sp-fac-search"
            placeholder="Search facilities…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="sp-fac-list">
            {filtered.map(f => (
              <div key={f} className="sp-fac-item" onClick={() => toggle(f)}>{f}</div>
            ))}
            {filtered.length === 0 && search && (
              <div className="sp-fac-item sp-fac-item-none">No match — use "Other" below</div>
            )}
          </div>
          {selected.length > 0 && (
            <div className="sp-fac-selected-section">
              <p className="sp-fac-section-label">Selected</p>
              <div className="sp-fac-selected-list">
                {selected.map(f => (
                  <div key={f} className="sp-fac-item sp-fac-item-selected" onClick={() => toggle(f)}>
                    <span className="sp-fac-check">✓</span> {f}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="sp-fac-other">
            <p className="sp-fac-section-label">Other (custom)</p>
            <div className="sp-fac-other-row">
              <input
                className="sp-fac-search"
                placeholder="Type a custom facility…"
                value={custom}
                onChange={e => setCustom(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
              />
              {custom.trim() && (
                <button type="button" className="sp-fac-add-btn" onClick={addCustom}>+ Add</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>("Basic Info");
  const [settings, setSettings]   = useState<Record<string, any>>({});
  const [form, setForm]           = useState<Record<string, any>>({});
  const [roomCount, setRoomCount] = useState<number>(0);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast]         = useState<{ msg: string; ok: boolean } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // ── FAQ state ─────────────────────────────────────────────
  const [faqs, setFaqs]             = useState<FaqEntry[]>([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [faqModal, setFaqModal]     = useState<"new" | FaqEntry | null>(null);
  const [faqForm, setFaqForm]       = useState({ faq_content: "", faq_summary: "", language: "English", active: true });
  const [faqSaving, setFaqSaving]   = useState(false);
  const [faqDeleting, setFaqDeleting] = useState<number | null>(null);

  const token       = localStorage.getItem("ai2_token") ?? "";
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const userRole: string = (() => {
    try { const d: any = jwtDecode(token); return (d.role || "").toLowerCase(); } catch { return ""; }
  })();
  const canEditFaqs = ["manager", "owner", "admin", "reception", "frontdesk"].includes(userRole);

  useEffect(() => {
    let hotelId = "";
    try { const d: any = jwtDecode(token); hotelId = d.hotelId || ""; } catch { /* */ }
    if (!hotelId) { setLoading(false); return; }

    Promise.all([
      axios.get(`${API_BASE}/api/hotel/settings/${hotelId}`),
      axios.get(`${API_BASE}/api/rooms`, { headers: authHeaders }),
    ])
      .then(([sRes, rRes]) => {
        const s = sRes.data || {};
        setSettings(s);
        setForm(s);
        setRoomCount((rRes.data || []).length);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key: string, val: any) =>
    setForm(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const { supabase_url, supabase_service_key, ...safeForm } = form;
      const res = await axios.put(`${API_BASE}/api/hotel/settings`, safeForm, { headers: authHeaders });
      setSettings(res.data);
      setForm(res.data);
      showToast("Saved successfully", true);
    } catch (err: any) {
      showToast(err?.response?.data?.message || "Failed to save", false);
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const res = await axios.post(`${API_BASE}/api/hotel/settings/upload-logo`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      handleChange("logo_url", res.data.url);
      showToast("Logo uploaded", true);
    } catch (err: any) {
      showToast(err?.response?.data?.message || "Upload failed", false);
    } finally {
      setUploading(false);
    }
  };

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  // ── FAQ helpers ───────────────────────────────────────────
  const loadFaqs = async () => {
    setFaqLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/api/settings/faqs`, { headers: authHeaders });
      setFaqs(data || []);
    } catch { /* silent */ }
    finally { setFaqLoading(false); }
  };

  const openFaqModal = (entry: FaqEntry | "new") => {
    if (entry === "new") {
      setFaqForm({ faq_content: "", faq_summary: "", language: "English", active: true });
    } else {
      setFaqForm({ faq_content: entry.faq_content || "", faq_summary: entry.faq_summary || "", language: entry.language || "English", active: entry.active !== false });
    }
    setFaqModal(entry);
  };

  const saveFaq = async () => {
    if (!faqForm.faq_summary.trim()) return;
    if (faqForm.faq_summary.length > FAQ_MAX) return;
    setFaqSaving(true);
    try {
      const payload = {
        faq_summary: faqForm.faq_summary.trim(),
        faq_content: faqForm.faq_content.trim() || faqForm.faq_summary.trim(),
        language: faqForm.language,
        active: faqForm.active,
      };
      if (faqModal === "new") {
        const { data } = await axios.post(`${API_BASE}/api/settings/faqs`, payload, { headers: authHeaders });
        setFaqs(prev => [...prev, data]);
      } else if (faqModal && typeof faqModal === "object") {
        const { data } = await axios.put(`${API_BASE}/api/settings/faqs/${faqModal.id}`, payload, { headers: authHeaders });
        setFaqs(prev => prev.map(f => f.id === (faqModal as FaqEntry).id ? data : f));
      }
      setFaqModal(null);
      showToast("FAQ saved", true);
    } catch (e: any) {
      showToast(e?.response?.data?.error || "Failed to save", false);
    } finally { setFaqSaving(false); }
  };

  const deleteFaq = async (id: number) => {
    setFaqDeleting(id);
    try {
      await axios.delete(`${API_BASE}/api/settings/faqs/${id}`, { headers: authHeaders });
      setFaqs(prev => prev.filter(f => f.id !== id));
      showToast("Deleted", true);
    } catch { showToast("Delete failed", false); }
    finally { setFaqDeleting(null); }
  };

  const toggleFaqActive = async (entry: FaqEntry) => {
    const updated = { ...entry, active: !entry.active };
    setFaqs(prev => prev.map(f => f.id === entry.id ? updated : f));
    try {
      await axios.put(`${API_BASE}/api/settings/faqs/${entry.id}`, { active: !entry.active }, { headers: authHeaders });
    } catch {
      setFaqs(prev => prev.map(f => f.id === entry.id ? entry : f));
    }
  };

  const moveFaq = async (index: number, dir: -1 | 1) => {
    const newFaqs = [...faqs];
    const target = index + dir;
    if (target < 0 || target >= newFaqs.length) return;
    [newFaqs[index], newFaqs[target]] = [newFaqs[target], newFaqs[index]];
    const reordered = newFaqs.map((f, i) => ({ ...f, sort_order: i }));
    setFaqs(reordered);
    try {
      await axios.put(`${API_BASE}/api/settings/faqs/reorder`,
        reordered.map(f => ({ id: f.id, sort_order: f.sort_order })),
        { headers: authHeaders }
      );
    } catch { loadFaqs(); }
  };

  // Load FAQs when switching to that tab
  useEffect(() => {
    if (activeTab === "Quick Answers" && faqs.length === 0) loadFaqs();
  }, [activeTab]);

  const activeFaqChars = faqs.filter(f => f.active).reduce((n, f) => n + (f.faq_summary?.length || 0), 0);

  const fp = { form, onChange: handleChange };

  return (
    <DashboardLayout>
      <div className="sp-page">

        {/* Header */}
        <div className="sp-header">
          <div>
            <h1 className="sp-title">Hotel Settings</h1>
            <p className="sp-subtitle">{settings.hotel_name || "Your Hotel"}</p>
          </div>
          <button className="sp-save-btn" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>

        {/* Tabs */}
        <div className="sp-tabs">
          {TABS.map(t => (
            <button
              key={t}
              className={`sp-tab${activeTab === t ? " active" : ""}`}
              onClick={() => setActiveTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="sp-loading">Loading settings…</div>
        ) : (
          <div className="sp-body">

            {/* ── BASIC INFO ─────────────────────────────────────────────── */}
            {activeTab === "Basic Info" && (
              <div className="sp-section">
                <h2 className="sp-section-title">Basic Info</h2>
                <div className="sp-grid">
                  <Field label="Hotel Name"  name="hotel_name"      placeholder="e.g. Sam's Story" {...fp} />
                  <SelectField label="Timezone" name="timezone" options={TIMEZONES} {...fp} />
                  <Field label="Address"     name="address"         span2 {...fp} />
                  <Field label="Locations"   name="location"        placeholder="Maadi, Nile, North Coast" span2 {...fp} />
                  <SelectField label="Default Language" name="default_language" options={LANGUAGES} {...fp} />
                  <Field label="Google Maps Link" name="google_maps_link" placeholder="https://maps.google.com/…" {...fp} />
                  <Field label="Instagram"   name="social_instagram" placeholder="https://instagram.com/…" span2 {...fp} />
                  <FacilitiesSelect {...fp} />

                  {/* Logo */}
                  <div className="sp-field sp-field-span2">
                    <label className="sp-label">Hotel Logo</label>
                    <div className="sp-logo-upload-row">
                      <div className="sp-logo-box">
                        {form.logo_url
                          ? <img src={form.logo_url} alt="Hotel Logo" className="sp-logo-img-preview" />
                          : <span className="sp-logo-placeholder">No Logo</span>}
                      </div>
                      <div className="sp-logo-upload-info">
                        <p className="sp-logo-upload-hint">PNG, JPG or SVG — max 5 MB</p>
                        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                          onChange={e => { const f = e.target.files?.[0]; if (f) uploadLogo(f); }} />
                        <button className="sp-upload-btn" disabled={uploading}
                          onClick={() => fileRef.current?.click()}>
                          {uploading ? "Uploading…" : "Upload Logo"}
                        </button>
                        <input type="text" className="sp-input" style={{ marginTop: 8 }}
                          value={form.logo_url ?? ""} placeholder="or paste URL"
                          onChange={e => handleChange("logo_url", e.target.value)} />
                      </div>
                    </div>
                  </div>

                  <TextArea label="Description" name="description" placeholder="Hotel description…" rows={6} {...fp} />
                </div>
              </div>
            )}

            {/* ── CONTACT ────────────────────────────────────────────────── */}
            {activeTab === "Contact" && (
              <div className="sp-section">
                <h2 className="sp-section-title">Contact</h2>
                <div className="sp-grid">
                  <Field label="Phone Number"       name="phone_number"       type="tel"   placeholder="+20…"               {...fp} />
                  <Field label="Email"              name="email"              type="email" placeholder="reservations@…"     {...fp} />
                  <Field label="Website URL"        name="website_url"        type="url"   placeholder="https://…"          {...fp} />
                  <Field label="General Contact"    name="general_contact"    type="email" placeholder="info@…"             {...fp} />
                  <Field label="Management Contact" name="management_contact" type="email" placeholder="manager@…"          {...fp} />
                  <Field label="Marketing Contact"  name="marketing_contact"  type="email" placeholder="marketing@…"        {...fp} />
                  <Field label="Front Desk"         name="front_desk"                      placeholder="ext. 0 / direct no" {...fp} />
                </div>
              </div>
            )}

            {/* ── OPERATIONS ─────────────────────────────────────────────── */}
            {activeTab === "Operations" && (
              <>
                {/* Payments */}
                <div className="sp-section">
                  <h2 className="sp-section-title">Payments</h2>
                  <div className="sp-grid">
                    <SelectField label="Currency"       name="default_currency" options={CURRENCIES} {...fp} />
                    <Field       label="Accepted Currencies" name="accepted_currencies" placeholder="EGP, USD" span2 {...fp} />
                    <Field       label="Invoice Prefix" name="invoice_prefix"   placeholder="SAM-"   {...fp} />
                    <PercentField label="Tax Rate"      name="tax_rate"    {...fp} />
                    <PercentField label="Service Charge" name="service_charge" {...fp} />
                    <Field        label="Deposit — days to hold" name="deposit_days" type="number" placeholder="2" {...fp} />
                    <PercentField label="Deposit %"     name="deposit_percentage" {...fp} />
                    <div className="sp-field">
                      <label className="sp-label">Paymob Online Payments</label>
                      <div className="sp-toggle-group">
                        {["Enabled", "Disabled"].map(o => {
                          const on = o === "Enabled";
                          const active = !!form.paymob_enabled === on;
                          return (
                            <button key={o} type="button" className={`sp-toggle-btn${active ? " active" : ""}`} onClick={() => handleChange("paymob_enabled", on)}>{o}</button>
                          );
                        })}
                      </div>
                    </div>
                    <PaymentMethodsSelect {...fp} />
                  </div>
                </div>

                {/* Bank Details */}
                <div className="sp-section">
                  <h2 className="sp-section-title">Bank Details</h2>
                  <p className="sp-section-hint">Shown to guests for bank transfers and printed on invoices.</p>
                  <div className="sp-grid">
                    <Field label="Bank Name"    name="bank_name"         placeholder="Banque Misr"        {...fp} />
                    <Field label="Account Name" name="bank_account_name" placeholder="Hotel Co."          {...fp} />
                    <Field label="Branch"       name="bank_branch"       placeholder="Nuweiba - 443"      {...fp} />
                    <Field label="SWIFT"        name="bank_swift"        placeholder="BMISEGCXXXX"        {...fp} />
                    <Field label="IBAN (EGP)"       name="iban_egp"       span2 {...fp} />
                    <Field label="Account No (EGP)" name="account_no_egp"       {...fp} />
                    <Field label="IBAN (EUR)"       name="iban_eur"       span2 {...fp} />
                    <Field label="Account No (EUR)" name="account_no_eur"       {...fp} />
                    <Field label="IBAN (USD)"       name="iban_usd"       span2 {...fp} />
                    <Field label="Account No (USD)" name="account_no_usd"       {...fp} />
                  </div>

                  {/* Additional accounts — any currency, optional per-account SWIFT. The bot lists these too. */}
                  <p className="sp-section-hint" style={{ marginTop: 16 }}>Additional accounts — add any currency (e.g. GBP, SAR). The bot lists every account automatically.</p>
                  {((form.extra_bank_accounts as any[]) || []).map((a: any, i: number) => {
                    const arr = (form.extra_bank_accounts as any[]) || [];
                    const upd = (k: string, v: string) => handleChange("extra_bank_accounts", arr.map((x, j) => j === i ? { ...x, [k]: v } : x));
                    return (
                      <div key={i} className="sp-grid" style={{ alignItems: "end", marginBottom: 10 }}>
                        <div className="sp-field"><label className="sp-label">Currency</label>
                          <input className="sp-input" placeholder="e.g. GBP" value={a.currency || ""} onChange={e => upd("currency", e.target.value.toUpperCase())} /></div>
                        <div className="sp-field"><label className="sp-label">SWIFT (optional)</label>
                          <input className="sp-input" placeholder="leave blank to use main SWIFT" value={a.swift || ""} onChange={e => upd("swift", e.target.value)} /></div>
                        <div className="sp-field sp-field-span2"><label className="sp-label">IBAN</label>
                          <input className="sp-input" value={a.iban || ""} onChange={e => upd("iban", e.target.value)} /></div>
                        <div className="sp-field"><label className="sp-label">Account No</label>
                          <input className="sp-input" value={a.account_no || ""} onChange={e => upd("account_no", e.target.value)} /></div>
                        <div className="sp-field"><label className="sp-label">&nbsp;</label>
                          <button type="button" className="sp-toggle-btn" onClick={() => handleChange("extra_bank_accounts", arr.filter((_, j) => j !== i))}>Remove</button></div>
                      </div>
                    );
                  })}
                  <button type="button" className="sp-toggle-btn" style={{ marginTop: 4 }}
                    onClick={() => handleChange("extra_bank_accounts", [...((form.extra_bank_accounts as any[]) || []), { currency: "", iban: "", account_no: "", swift: "" }])}>
                    + Add account
                  </button>
                </div>

                {/* Offers */}
                <div className="sp-section">
                  <h2 className="sp-section-title">Offers &amp; Discounts</h2>
                  <p className="sp-section-hint">Add the promotions or special deals available at your hotel.</p>
                  <div className="sp-grid">
                    <OffersEditor {...fp} />
                  </div>
                </div>

                {/* Policies */}
                <div className="sp-section">
                  <h2 className="sp-section-title">Policies</h2>
                  <div className="sp-grid">
                    <Field label="Check In"  name="check_in"  type="time" {...fp} />
                    <Field label="Check Out" name="check_out" type="time" {...fp} />
                    <TextArea
                      label="Cancellation Policy"
                      name="refund_policy"
                      placeholder="e.g. Free cancellation up to 48h before check-in…"
                      rows={3}
                      {...fp}
                    />
                    <ToggleGroup
                      label="Smoking"
                      name="smoking_policy"
                      options={["Non-smoking", "Smoking allowed", "Designated areas"]}
                      allowCustom
                      {...fp}
                    />
                    <ToggleGroup
                      label="Pets"
                      name="pets_policy"
                      options={["Not allowed", "Allowed", "Small pets only"]}
                      allowCustom
                      {...fp}
                    />
                  </div>
                </div>

                {/* Rooms (auto-synced) */}
                <div className="sp-section">
                  <h2 className="sp-section-title">Rooms <span className="sp-auto-badge" style={{ fontSize: 10, verticalAlign: "middle" }}>Auto-synced</span></h2>
                  <p className="sp-section-hint">These values are kept in sync automatically whenever rooms are added, edited, or removed.</p>
                  <div className="sp-grid">
                    <AutoField label="Live Room Count"        value={roomCount} />
                    <AutoField label="Unique Room Types"      value={settings.rooms_count ?? "—"} />
                  </div>
                </div>
              </>
            )}

            {/* ── AI ─────────────────────────────────────────────────────── */}
            {activeTab === "AI" && (
              <div className="sp-section">
                <h2 className="sp-section-title">AI Concierge</h2>
                <div className="sp-grid">
                  <Field label="Agent Name"          name="ai_agent_name"       placeholder="Eva Concierge"    {...fp} />
                  <Field label="Version"             name="ai_version"          readOnly                       {...fp} />
                  <TextArea label="Personality / Tone" name="ai_personality"    placeholder="Calm, poetic, professional…" rows={3} {...fp} />
                  <Field label="Active Modules"      name="active_modules"      placeholder="reception, spa, restaurant…" span2 {...fp} />
                  <Field label="Response Delay (ms)" name="response_delay_ms"   type="number" placeholder="1500" {...fp} />
                  <SelectField label="Automation Timezone" name="timezone_automation" options={TIMEZONES}       {...fp} />
                </div>
              </div>
            )}

            {/* ── QUICK ANSWERS / FAQ ─────────────────────────────────── */}
            {activeTab === "Quick Answers" && (
              <div className="sp-section">
                <div className="faq-header-row">
                  <div>
                    <h2 className="sp-section-title">Quick Answers / FAQ</h2>
                    <p className="sp-section-hint">
                      The bot reads these top-to-bottom and injects them into every conversation.
                      Keep entries concise — total active text is capped at {FAQ_TOTAL_CAP} characters.
                    </p>
                  </div>
                  {canEditFaqs && (
                    <button className="faq-add-btn" onClick={() => openFaqModal("new")}>+ Add Entry</button>
                  )}
                </div>

                {/* Total usage bar */}
                <div className="faq-usage-bar-wrap">
                  <div className="faq-usage-labels">
                    <span>Active characters used by bot</span>
                    <span className={activeFaqChars > FAQ_TOTAL_CAP ? "faq-usage-over" : activeFaqChars > FAQ_TOTAL_CAP * 0.8 ? "faq-usage-warn" : "faq-usage-ok"}>
                      {activeFaqChars} / {FAQ_TOTAL_CAP}
                    </span>
                  </div>
                  <div className="faq-usage-track">
                    <div
                      className={`faq-usage-fill${activeFaqChars > FAQ_TOTAL_CAP ? " over" : activeFaqChars > FAQ_TOTAL_CAP * 0.8 ? " warn" : ""}`}
                      style={{ width: `${Math.min(100, (activeFaqChars / FAQ_TOTAL_CAP) * 100)}%` }}
                    />
                  </div>
                </div>

                {faqLoading ? (
                  <div className="sp-loading" style={{ padding: "40px 0" }}>Loading FAQ entries…</div>
                ) : faqs.length === 0 ? (
                  <div className="faq-empty">
                    <span>💬</span>
                    <p>No FAQ entries yet. Add your first quick answer above.</p>
                  </div>
                ) : (
                  <div className="faq-list">
                    {faqs.map((faq, idx) => (
                      <div key={faq.id} className={`faq-row${!faq.active ? " faq-row-inactive" : ""}`}>
                        {canEditFaqs && (
                          <div className="faq-order-btns">
                            <button className="faq-order-btn" onClick={() => moveFaq(idx, -1)} disabled={idx === 0} title="Move up">▲</button>
                            <span className="faq-order-num">{idx + 1}</span>
                            <button className="faq-order-btn" onClick={() => moveFaq(idx, 1)} disabled={idx === faqs.length - 1} title="Move down">▼</button>
                          </div>
                        )}
                        {canEditFaqs && (
                          <button
                            className={`faq-toggle${faq.active ? " faq-toggle-on" : " faq-toggle-off"}`}
                            onClick={() => toggleFaqActive(faq)}
                            title={faq.active ? "Disable" : "Enable"}
                            aria-label="Toggle active"
                          >
                            <span className="faq-toggle-knob" />
                          </button>
                        )}
                        <div className="faq-row-body">
                          <div className="faq-row-question">
                            {faq.faq_content && faq.faq_content !== faq.faq_summary
                              ? faq.faq_content
                              : (faq.faq_summary.length > 70 ? faq.faq_summary.slice(0, 70) + "…" : faq.faq_summary)}
                          </div>
                          <div className="faq-row-summary">{faq.faq_summary}</div>
                          <div className="faq-row-meta">
                            <span className="faq-lang-badge">{faq.language}</span>
                            <span className="faq-char-count">{faq.faq_summary.length} chars</span>
                            {!faq.active && <span className="faq-disabled-badge">Disabled</span>}
                          </div>
                        </div>
                        {canEditFaqs && (
                          <div className="faq-row-actions">
                            <button className="faq-edit-btn" onClick={() => openFaqModal(faq)}>Edit</button>
                            <button className="faq-del-btn" onClick={() => deleteFaq(faq.id)} disabled={faqDeleting === faq.id}>
                              {faqDeleting === faq.id ? "…" : "×"}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {!canEditFaqs && (
                  <div className="faq-readonly-note">Read-only — contact a manager to make changes.</div>
                )}
              </div>
            )}

            {/* ── INTEGRATIONS ───────────────────────────────────────────── */}
            {activeTab === "Integrations" && (
              <div className="sp-section">
                <h2 className="sp-section-title">Integrations</h2>

                <h3 className="sp-sub-title" style={{ marginTop: 4 }}>Webhook</h3>
                <div className="sp-grid">
                  <Field label="Webhook URL" name="trigger_webhook_url" placeholder="https://…/webhook/bookings" span2 {...fp} />
                </div>

                <h3 className="sp-sub-title" style={{ marginTop: 28 }}>Paymob</h3>
                <div className="sp-grid">
                  <Field label="Paymob API Key" name="paymob_api"    type="password" placeholder="Paymob API key" span2 {...fp} />
                  <Field label="iFrame ID"       name="paymob_iframe" type="number"   placeholder="1005658"              {...fp} />
                </div>

                <h3 className="sp-sub-title" style={{ marginTop: 28 }}>Google Sheets</h3>
                <div className="sp-grid">
                  <AutoField label="Sheet ID"   value={settings.sheet_id       ?? "—"} />
                  <AutoField label="Guests GID" value={settings.integration_id ?? "—"} />
                </div>

                <h3 className="sp-sub-title" style={{ marginTop: 28 }}>Hotel Database</h3>
                <div className="sp-grid">
                  <StatusBadge label="Supabase URL" configured={!!settings.supabase_url} />
                  <StatusBadge label="Supabase Key" configured={!!settings.supabase_service_key} />
                </div>

                <div className="sp-info-box" style={{ marginTop: 16 }}>
                  <strong>Hotel ID</strong>
                  <code>{settings.id || "—"}</code>
                </div>
              </div>
            )}

          </div>
        )}

        {toast && (
          <div className={`sp-toast${toast.ok ? "" : " sp-toast-error"}`}>{toast.msg}</div>
        )}

        {/* ── FAQ Modal ── */}
        {faqModal !== null && (
          <div className="faq-modal-overlay" onClick={() => setFaqModal(null)}>
            <div className="faq-modal" onClick={e => e.stopPropagation()}>
              <div className="faq-modal-header">
                <h3>{faqModal === "new" ? "New FAQ Entry" : "Edit FAQ Entry"}</h3>
                <button className="faq-modal-close" onClick={() => setFaqModal(null)}>✕</button>
              </div>

              {/* Question label */}
              <div className="faq-field">
                <label className="faq-field-label">
                  Question label <span className="faq-field-hint">(admin only — not sent to bot)</span>
                </label>
                <input
                  className="faq-input"
                  placeholder="e.g. Where is the spa?"
                  value={faqForm.faq_content}
                  onChange={e => setFaqForm(f => ({ ...f, faq_content: e.target.value }))}
                />
              </div>

              {/* Bot text */}
              <div className="faq-field">
                <div className="faq-field-label-row">
                  <label className="faq-field-label">
                    Bot text <span className="faq-field-hint">(injected into AI prompt)</span>
                  </label>
                  <span className={`faq-char-counter ${faqForm.faq_summary.length > FAQ_MAX ? "over" : faqForm.faq_summary.length > FAQ_WARN ? "warn" : ""}`}>
                    {faqForm.faq_summary.length} / {FAQ_MAX}
                  </span>
                </div>
                <textarea
                  className={`faq-textarea ${faqForm.faq_summary.length > FAQ_WARN ? faqForm.faq_summary.length > FAQ_MAX ? "faq-textarea-error" : "faq-textarea-warn" : ""}`}
                  placeholder="e.g. The spa is located on the 3rd floor and is open daily from 9 AM to 9 PM. Guests can book appointments at reception or via WhatsApp."
                  rows={4}
                  maxLength={FAQ_MAX}
                  value={faqForm.faq_summary}
                  onChange={e => setFaqForm(f => ({ ...f, faq_summary: e.target.value.slice(0, FAQ_MAX) }))}
                />
                {faqForm.faq_summary.length > FAQ_WARN && faqForm.faq_summary.length <= FAQ_MAX && (
                  <p className="faq-warn-msg">⚠ Approaching limit — consider shortening this entry</p>
                )}
                {faqForm.faq_summary.length > FAQ_MAX && (
                  <p className="faq-error-msg">✗ Exceeds 500 character limit</p>
                )}
              </div>

              {/* Preview */}
              {faqForm.faq_summary.trim() && (
                <div className="faq-preview-box">
                  <span className="faq-preview-label">This is what the bot sees</span>
                  <p className="faq-preview-text">{faqForm.faq_summary}</p>
                </div>
              )}

              {/* Language + Active row */}
              <div className="faq-modal-meta">
                <div className="faq-field" style={{ flex: 1 }}>
                  <label className="faq-field-label">Language</label>
                  <select className="faq-select" value={faqForm.language} onChange={e => setFaqForm(f => ({ ...f, language: e.target.value }))}>
                    {FAQ_LANGS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="faq-field" style={{ flex: 0 }}>
                  <label className="faq-field-label">Active</label>
                  <button
                    className={`faq-toggle${faqForm.active ? " faq-toggle-on" : " faq-toggle-off"}`}
                    onClick={() => setFaqForm(f => ({ ...f, active: !f.active }))}
                    style={{ marginTop: 4 }}
                  >
                    <span className="faq-toggle-knob" />
                  </button>
                </div>
              </div>

              <div className="faq-modal-actions">
                <button
                  className="faq-save-btn"
                  onClick={saveFaq}
                  disabled={faqSaving || !faqForm.faq_summary.trim() || faqForm.faq_summary.length > FAQ_MAX}
                >
                  {faqSaving ? "Saving…" : faqModal === "new" ? "Add Entry" : "Save Changes"}
                </button>
                <button className="faq-cancel-btn" onClick={() => setFaqModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
